import * as React from 'react'
import { type TargetsParam, animate } from 'animejs'
import { useDispatch } from 'react-redux'
import { throttle } from '@mobi/utils/functions'
import { toggleIsAppHeaderVisible } from '@core/Areas/AppHeader/Store'

export const useStickyOnScroll = ({
  appHeaderRef,
  isVisible,
  shouldDisableSticky,
}: {
  appHeaderRef: React.RefObject<HTMLDivElement | null>
  isVisible: boolean
  shouldDisableSticky: boolean
}): void => {
  const dispatch = useDispatch()
  const scrollYCurrentRef = React.useRef(0)
  const scrollYPreviousRef = React.useRef(0)

  React.useEffect(() => {
    if (shouldDisableSticky) return

    const updateStickyState = () => {
      scrollYCurrentRef.current = window.pageYOffset

      if (scrollYCurrentRef.current < 61) {
        dispatch(toggleIsAppHeaderVisible(true))
        return
      }

      if (isBottomOverscroll(scrollYCurrentRef.current)) {
        return
      }

      if (scrollYCurrentRef.current > 60) {
        const scrollDirection =
          scrollYCurrentRef.current >= scrollYPreviousRef.current ? 'down' : 'up'
        const distanceScrolled = Math.abs(scrollYCurrentRef.current - scrollYPreviousRef.current)

        const upTolerance = 5
        const downTolerance = 15

        if (
          // Going UP & not pinned, set to pinned
          scrollDirection === 'up' &&
          distanceScrolled > upTolerance
        ) {
          dispatch(toggleIsAppHeaderVisible(true))
        } else if (
          // Going DOWN & already pinned, set to unpinned
          scrollDirection === 'down' &&
          distanceScrolled > downTolerance
        ) {
          dispatch(toggleIsAppHeaderVisible(false))
        }
      }
      scrollYPreviousRef.current = scrollYCurrentRef.current
    }

    const watchScrollEvent = throttle(updateStickyState, 70)
    window.addEventListener('scroll', watchScrollEvent)

    return () => {
      watchScrollEvent.cancel()
      window.removeEventListener('scroll', watchScrollEvent)
    }
  }, [dispatch, shouldDisableSticky])

  // Force display of AppHeader on request
  React.useEffect(() => {
    hideOrShowAppHeader({ appHeaderRef, isVisible })
  }, [appHeaderRef, isVisible])

  React.useEffect(() => {
    if (!shouldDisableSticky) return
    hideOrShowAppHeader({ appHeaderRef, isVisible: true })
  }, [appHeaderRef, shouldDisableSticky])
}

// =============
// Local Helpers
// =============

const hideOrShowAppHeader = ({
  appHeaderRef,
  isVisible,
}: {
  appHeaderRef: React.RefObject<HTMLDivElement | null>
  isVisible: boolean
}): void => {
  animate(appHeaderRef.current as TargetsParam, {
    translateY: isVisible ? ['-100%', 0] : [0, '-100%'],
    ease: 'inOutQuad',
    duration: 125,
  })
}

function isBottomOverscroll(currentScrollY: number) {
  return currentScrollY + window.innerHeight > document.body.scrollHeight
}
